import ErrorBoundary from "@/shared/components/error-boundary";
import { Spinner, Tab, Tabs } from "@nextui-org/react";
import Overview from "./Overview/Overview";
import PotentialIssues from "./PotentialIssues/PotentialIssues";
import SupplyChain from "./SupplyChain/SupplyChain";

import { AgentSessionStatus, AgentSessionStepType, MultiVendorAgentSession } from '@/modules/sessions/types';
import VendorLogo from "@/shared/components/VendorSearchAutocomplete/VendorLogo";
import useEffectAsync from '@/shared/hooks/useEffectAsync';
import parsePromise from '@/shared/utils/parsePromise';
import clsx from 'clsx';
import { Calendar, Loader, User } from 'lucide-react';
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTeamsListQuery } from "@/modules/auth/requests";
import { formatDate } from "@/modules/third-party-risk/pages/vendors-v2/utils/formatters";
import { vendorDetailKeys } from "@modules/third-party-risk/pages/vendor-detail/requests";
import {
  tpraAssessmentsQueryKeys,
  tpraVendorsQueryKeys,
  tpraHomeQueryKeys,
  breachHistoryQueryKeys,
  risksQueryKeys,
} from "@constants/QueryKeys";
import { getAgentData, getAgentStateActions, useAgentSelector, useAgentSessionData, useAgentStepData } from '../../states';
import { VendorRiskSectionEnum } from '../../types/vendorAssessment';
import { VendorRiskResponseItemForVendorOverview } from '../../types/vendorOverviewSection';
import ReviewResponses from '../review-responses';
import { VendorRiskLevel } from './Overview/OverviewCards.types';
import RiskDropdown from './Overview/RiskDropdown';
import ReassessUpdateCard from "./ReassessUpdateCard";
import { useVendorRiskAssessment } from './useVendorRiskAssessment/useVendorRiskAssessment';
import { useVendorRiskAssessmentStore, VendorPageType } from './useVendorRiskAssessment/useVendorRiskAssessmentStore';
// Socket hooks - centralized at parent level to prevent duplicate connections across tabs
import { getZaniaSocket } from "@/infra/sockets";
import { useControlEventsSubscription } from "@/modules/agent/hooks/useControlEventsSubscription";
import { useControlStatus } from "@/modules/agent/hooks/useControlStatus";
import { useSessionSubscription } from "@/modules/agent/hooks/useSessionSubscription";
import { AgentSocketEvents } from "../../types/events";

const invalidateVendorRiskRelatedQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: tpraAssessmentsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: tpraVendorsQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: tpraHomeQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: vendorDetailKeys.all });
  queryClient.invalidateQueries({ queryKey: breachHistoryQueryKeys.all });
  queryClient.invalidateQueries({ queryKey: risksQueryKeys.all });
};

interface Params {
  id: string;
}

const VendorRiskAssessment = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isSaving } = useVendorRiskAssessmentStore();
  const {
    combinedVendorRiskData,
    selectedTab,
    handleTabChange,
    loadVendorRiskData,
    vendorOverview,
  } = useVendorRiskAssessment();

  const { reset } = useVendorRiskAssessmentStore();
  const queryClient = useQueryClient();
  const { setSelectedId } = useAgentSelector.useActions();

  // Add state to track the risk level for live updates
  const [liveRiskLevel, setLiveRiskLevel] = useState<VendorRiskLevel | null>(null);
  const changesMadeRef = useRef(0);

  useEffectAsync(async () => {
    await parsePromise(loadVendorRiskData());
  }, [loadVendorRiskData]);

  // Monitor vendor overview for changes to risk_rating
  useEffect(() => {
    const riskRatingItem = vendorOverview.find(item => item.key === "risk_rating") as
      VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    if (riskRatingItem?.value?.value) {
      // Verify risk level is a valid VendorRiskLevel value
      const riskValue = riskRatingItem.value.value;
      if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
        setLiveRiskLevel(riskValue);
      }
    }
  }, [vendorOverview]);

  // Subscribe to store changes to detect remote updates
  useEffect(() => {
    const unsubscribe = useVendorRiskAssessmentStore.subscribe((state) => {
      // Only update if the changesMade value has increased (indicating a store update)
      if (state.changesMade !== changesMadeRef.current) {
        changesMadeRef.current = state.changesMade;

        // Optimize: only check risk rating if vendorOverview actually changed
        if (state.vendorOverview.length > 0) {
          const riskRatingItem = state.vendorOverview.find(item => item.key === "risk_rating") as
            VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

          if (riskRatingItem?.value?.value) {
            const riskValue = riskRatingItem.value.value;
            if (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") {
              setLiveRiskLevel(riskValue);
            }
          }
        }
      }
    });

    return unsubscribe;
  }, []);

  const [reassessUpdateCardVisible, setReassessUpdateCardVisible] = useState(true);

  const sessionData = useAgentSessionData(id);

  // Fetch team data for created by user name
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: !!sessionData });

  useEffect(() => {
    return () => {
      // IMPORTANT: Read changesMade BEFORE calling reset() which sets it back to 0
      const { changesMade } = useVendorRiskAssessmentStore.getState();
      if (changesMade > 0) {
        invalidateVendorRiskRelatedQueries(queryClient);
      }
      reset();
    };
  }, [reset, queryClient]);

  // Extract risk level calculation into a memoized value that will update from either
  // combinedVendorRiskData or liveRiskLevel changes
  const currentRiskLevel = useMemo(() => {
    // If we have a live risk level from the direct store updates, use that
    if (liveRiskLevel) {
      return liveRiskLevel;
    }

    // Otherwise, fall back to the combinedVendorRiskData method
    if (!combinedVendorRiskData) return null;

    const riskRatingItem = combinedVendorRiskData[VendorRiskSectionEnum.vendor_overview]?.find(
      (item: any) => item.key === "risk_rating"
    ) as VendorRiskResponseItemForVendorOverview<"risk_rating"> | undefined;

    const riskValue = riskRatingItem?.value.value;
    return (riskValue === "High" || riskValue === "Medium" || riskValue === "Low") ? riskValue : null;
  }, [combinedVendorRiskData, liveRiskLevel]);

  // Derived values for header
  const vendorName = (sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.vendor_name ?? "";
  const vendorLogoUrl = (sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.vendor_logo_url ?? "";
  const productName = (sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.product_name || "All Products";
  const createdDate = formatDate((sessionData as MultiVendorAgentSession)?.created_at ?? null);
  const createdByName = teamsData?.memberIdNameMapping?.[(sessionData as MultiVendorAgentSession)?.created_by ?? ""] || "Unknown";
  const activeAssessmentCount = (sessionData as MultiVendorAgentSession)?.multi_file_vendor_assessment?.active_assessments_count;

  // Navigation handler for View Details
  const navigate = useNavigate();
  const handleViewDetails = useCallback(() => {
    if (vendorName) {
      navigate(`/third-party-risk/vendor/${encodeURIComponent(vendorName)}`);
    }
  }, [navigate, vendorName]);

  const stepData = useAgentStepData(id);
  const isReportReady =
    stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT)
      ?.status === AgentSessionStatus.COMPLETE;
  const criticality = vendorOverview.find(
        (overviewItem) => overviewItem.key === "business_criticality"
      );

  // Initialize socket connections at parent level to prevent duplicate connections
  // when switching between tabs (Overview, Company, Supply Chain, etc.)
  // These hooks manage session-level socket events and should only be initialized once
  useControlStatus(id);
  useSessionSubscription(id);
  useControlEventsSubscription(id);

  // Listen for socket update events to reset saving state AND handle live updates
  useEffect(() => {
    const socket = getZaniaSocket();
    
    const handleControlUpdated = (data: any) => {
      // Check if the update is for this session
      if (data.agent_session_id !== id) return;
      
      const store = useVendorRiskAssessmentStore.getState();
      
      // Always reset saving state when we get confirmation from server
      store.setIsSaving(false);
      
      // Handle live updates for vendor assessment controls
      if (data.control?.section) {
        const { control } = data;
        const controlSection = control.section as VendorRiskSectionEnum;
        
        // ============ PREVENT CURSOR JUMPS & TEXT LOSS ============
        // Get the currently selected/active control ID
        const agentData = getAgentData(id);
        const selectedId = agentData?.mainData?.selectedId;
        
        // CHECK 1: Is user actively typing in this control's input field?
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && 
          (activeElement.id?.includes(control.key) || 
           activeElement.getAttribute('aria-label')?.includes(control.key));
        
        // CHECK 2: Is this update very recent (within last 2 seconds)?
        const lastUpdate = agentData?.mainData?.lastUpdate;
        const isRecentLocalUpdate = lastUpdate && 
          !lastUpdate.isRemoteUpdate && 
          (Date.now() - lastUpdate.timestamp) < 2000;
        
        // SKIP update if:
        // - Input field is currently focused (prevents cursor jumps during typing), OR
        // - User just made a local edit (prevents self-echo)
        if (isInputFocused || (selectedId === control.key && isRecentLocalUpdate)) {
          console.log('[SOCKET] Skipping update - user actively editing:', control.key, 
            'focused:', !!isInputFocused, 'recent:', !!isRecentLocalUpdate);
          return;
        }
        
        // Mark this as a remote update for tracking
        if (agentData) {
          const { setAgentData } = getAgentStateActions();
          setAgentData(id, {
            ...agentData,
            mainData: {
              ...agentData.mainData,
              lastUpdate: {
                timestamp: Date.now(),
                isRemoteUpdate: true
              }
            }
          });
        }
        // =========================================================
        
        switch (controlSection) {
          case VendorRiskSectionEnum.vendor_overview: {
            const updatedOverview = [...store.vendorOverview];
            const overviewIndex = updatedOverview.findIndex((item) => item.key === control.key);
            
            if (overviewIndex !== -1) {
              if (control.key === "data_types_handled") {
                updatedOverview[overviewIndex] = {
                  ...updatedOverview[overviewIndex],
                  value: control.value,
                };
              } else {
                updatedOverview[overviewIndex] = {
                  ...updatedOverview[overviewIndex],
                  ...control,
                };
              }
              store.setVendorOverview(updatedOverview, true); // true = don't increment changesMade
            }
            break;
          }
          
          case VendorRiskSectionEnum.company_profile: {
            const updatedCompanyProfile = [...store.vendorCompanyData];
            const companyProfileIndex = updatedCompanyProfile.findIndex(
              (item) => item.key === control.key
            );
            
            if (companyProfileIndex !== -1) {
              updatedCompanyProfile[companyProfileIndex] = {
                ...updatedCompanyProfile[companyProfileIndex],
                ...control,
                edited: true,
              };
              store.setVendorCompanyData(updatedCompanyProfile, true);
            }
            break;
          }
          
          case VendorRiskSectionEnum.vendor_supply_chain: {
            const updatedSupplyChain = [...store.vendorSupplyChainData];
            const supplyChainIndex = updatedSupplyChain.findIndex(
              (item) => item.key === control.key
            );
            
            if (supplyChainIndex !== -1) {
              updatedSupplyChain[supplyChainIndex] = {
                ...updatedSupplyChain[supplyChainIndex],
                ...control,
                edited: true,
              };
              store.setVendorSupplyChain(updatedSupplyChain, true);
            }
            break;
          }
          
          case VendorRiskSectionEnum.potential_issues: {
            const updatedPotentialIssues = [...store.vendorPotentialIssuesData];
            const potentialIssuesIndex = updatedPotentialIssues.findIndex(
              (item) => item.key === control.key
            );
            
            if (potentialIssuesIndex !== -1) {
              updatedPotentialIssues[potentialIssuesIndex] = {
                ...updatedPotentialIssues[potentialIssuesIndex],
                ...control,
                edited: true,
              };
              store.setVendorPotentialIssues(updatedPotentialIssues, true);
            }
            break;
          }
        }
      }
    };

    socket.on(AgentSocketEvents.CONTROL_UPDATED, handleControlUpdated);

    return () => {
      socket.off(AgentSocketEvents.CONTROL_UPDATED, handleControlUpdated);
    };
  }, [id]);

  // Risk level change handler lifted from Overview component
  const handleRiskLevelChange = useCallback((newRiskLevel: VendorRiskLevel) => {
    const store = useVendorRiskAssessmentStore.getState();

    // Find the risk_rating item in vendor overview
    const indexOfOverviewItem = vendorOverview.findIndex(
      (overviewItem) => overviewItem.key === "risk_rating"
    );

    if (indexOfOverviewItem === -1) return;

    // Create updated overview array (deep copy to trigger changesMade)
    const newVendorOverview = vendorOverview.map((item, idx) => {
      if (idx === indexOfOverviewItem) {
        return {
          ...item,
          value: {
            ...item.value,
            value: newRiskLevel
          }
        };
      }
      return item;
    });

    // Set selectedId for autosave tracking (CRITICAL)
    setSelectedId(id, "risk_rating");

    // Set saving state
    store.setIsSaving(true);

    // Update store (triggers changesMade increment → autosave)
    store.setVendorOverview(newVendorOverview);

    // Update combined data
    if (store.combinedVendorRiskData) {
      const updatedCombinedData = {
        ...store.combinedVendorRiskData,
        [VendorRiskSectionEnum.vendor_overview]: newVendorOverview
      };
      store.setCombinedVendorRiskData(updatedCombinedData);
    }

    // Note: autosave is triggered by changesMade counter increment in setVendorOverview
  }, [vendorOverview, id, setSelectedId]);

  return combinedVendorRiskData ? (
    <ErrorBoundary fallback={null}>
      <div className="h-full w-full rounded-lg flex flex-col">
        {/* header */}
        <div className="bg-white sticky top-0 z-50 w-full">
          <div className="px-6 pt-4 pb-3 flex flex-col gap-4 w-full bg-white">
            {/* Row 1: Vendor Info */}
            <div className="flex flex-row items-center bg-[#F8FAFC] rounded-lg py-2 px-3 gap-2.5">
              {/* Logo (36x36px) */}
              {vendorLogoUrl && (
                <VendorLogo
                  alt={vendorName}
                  src={vendorLogoUrl}
                  className="size-9 rounded-full"
                  radius="sm"
                  needsAuthToken
                />
              )}

              {/* Vendor Name + Assessment Count + View Details */}
              <div className="flex flex-row items-baseline gap-2">
                <span className="font-semibold text-base leading-6 text-[#0F172B]">{vendorName}</span>
                {activeAssessmentCount != null && (
                  <span className="text-xs leading-4 font-normal text-[#62748E]">
                    &bull; {activeAssessmentCount} {activeAssessmentCount === 1 ? "assessment" : "assessments"}
                  </span>
                )}
                <button
                  onClick={handleViewDetails}
                  className="text-xs leading-4 font-medium text-[#006FEE] hover:underline cursor-pointer"
                >
                  View Details
                </button>
              </div>
            </div>

            {/* Row 2: Product & Metadata */}
            <div className="flex flex-row items-center justify-between">
              {/* Left: Product + Risk Badge + Saving Indicator */}
              <div className="flex flex-row items-center gap-3 min-w-0">
                <h1 className="text-xl font-semibold text-[#0F172B] whitespace-nowrap">{productName}</h1>
                {currentRiskLevel && (
                  <div className="flex-shrink-0">
                    <RiskDropdown
                      riskLevel={currentRiskLevel}
                      onRiskLevelChange={handleRiskLevelChange}
                      isDisabled={isReportReady}
                    />
                  </div>
                )}

                {/* Saving Indicator (after risk badge) */}
                <div className="flex items-center h-7 gap-1">
                  <Loader className={`w-4 h-4 animate-spin ${isSaving ? '' : 'invisible'}`} />
                  {isSaving && <p className="text-tiny text-[#A5A5A5] text-[10px]">Saving</p>}
                </div>
              </div>

              {/* Right: Created On + Created By */}
              <div className="flex flex-row items-center gap-6">
                {/* Created On */}
                <div className="flex flex-row items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-[#62748E]" />
                  <span className="text-xs text-[#62748E]">Created on</span>
                  <span className="text-xs font-medium text-[#415269]">{createdDate}</span>
                </div>

                {/* Created By */}
                <div className="flex flex-row items-center gap-1.5">
                  <User className="w-4 h-4 text-[#62748E]" />
                  <span className="text-xs text-[#62748E]">Created by</span>
                  <span className="text-xs font-medium text-[#415269]">{createdByName}</span>
                </div>
              </div>
            </div>
          </div>

          <Tabs
            variant="underlined"
            selectedKey={selectedTab}
            classNames={{
              tab: "rounded-none px-4 text-[12px] text-[#18181B]",
              base: "rounded-none",
              cursor: "rounded-none",
              panel: "rounded-none",
              tabContent: "rounded-none",
              tabList: "rounded-none p-0",
              wrapper: "rounded-none",
            }}
            className="border-b-1 bg-white w-full"
            onSelectionChange={(key) => handleTabChange(key as VendorPageType)}
          >
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t  after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "OVERVIEW",
              })}
              key="OVERVIEW"
              title="Overview"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "SUPPLY_CHAIN",
              })}
              key="SUPPLY_CHAIN"
              title="Supply Chain"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "DETAILED_REVIEW",
              })}
              key="DETAILED_REVIEW"
              title="Detailed Review"
            />
            <Tab
              className={clsx("rounded-none", {
                'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                  selectedTab === "POTENTIAL_ISSUES",
              })}
              key="POTENTIAL_ISSUES"
              title="Issues"
            />
          </Tabs>
        </div>
        <div className="h-full overflow-x-auto bg-white">
          {selectedTab === "OVERVIEW" && <Overview />}
          {selectedTab === "SUPPLY_CHAIN" && <SupplyChain />}
          {selectedTab === "DETAILED_REVIEW" && (
            <ReviewResponses
              hideAgentTypeFromHeader={true}
              disableRoundedBorder={true}
            />
          )}
          {selectedTab === "POTENTIAL_ISSUES" && <PotentialIssues />}
        </div>
      </div>
    </ErrorBoundary>
  ) : (
    <div className="w-full h-full bg-white flex items-center justify-center px-24">
      <Spinner />
    </div>
  );
};

export default VendorRiskAssessment;
